import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import type { TextLinkSize, TextLinkTarget } from './text-link.model';
import styles from './text-link.styles';
import { SlotController } from '../../controllers/slot-controller';

/**
 * @slot default - The component content
 * @slot linkAfter - Text after the link
 * @slot custom-link - The component's custom link slot
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart after - The component's after wrapper.
 * @csspart link-after - The component's link-after slot.
 * @csspart part-custom-link - The component's custom link slot
 */
export class BlocksTextLink extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * The link URL
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Specifies where to open the linked document
   * _blank -	Opens the linked document in a new window or tab
   * _self - Opens the linked document in the same frame as it was clicked (this is default)
   * _parent - Opens the linked document in the parent frame
   * _top	Opens - the linked document in the full body of the window
   * framename - Opens the linked document in the named iframe
   */
  @property({ type: String })
  target: TextLinkTarget = '_self';

  /**
   * Size variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li> **xs** renders a link with the footnote typography preset</li>
   * <li> **sm** renders a link with the body-sm typography preset</li>
   * <li> The `lg` and `xl` size variant is marked as **deprecated** and that will be removed in the next major release. </li>
   * @deprecated
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: TextLinkSize;

  /**
   * Inverted variant
   * 1) Used for dark backgrounds
   */
  @property({ type: Boolean })
  inverted: boolean | undefined;

  /**
   * Indicates whether the link is a skip link
   */
  @property({ type: Boolean })
  isSkipLink = false;

  /**
   * Aria label for the link for accessibility
   * 1) This should contain a readout of the data the link is representing.
   */
  @property({ type: String })
  arialabel: string | undefined;

  protected slots = new SlotController(this, null, 'linkAfter', 'custom-link');

  /**
   * Handle on keydown
   * 1) If key selected is enter, navigate to the custom link
   */

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === KEYS.ENTER) {
      this._children?.[0].click();
    }
  }

  /**
   * Handles click action
   * 1) programmatically focuses the target element.
   */
  private handleClick(e: MouseEvent) {
    if (this.href) {
      e.preventDefault();
      const target = document.querySelector<HTMLElement>(this.href);
      if (target) {
        // setting tabindex to make the target element focusable
        target.setAttribute('tabindex', '-1');
        target.addEventListener(
          'blur',
          () => {
            const tabIndex = target.getAttribute('tabindex');
            if (tabIndex === '-1') {
              target.removeAttribute('tabindex');
            }
          },
          { once: true }
        );
        target.focus();
      } else {
        console.error(`Target element with id ${this.href} not found`);
      }
    }
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-text-link', {
      'cat-c-text-link--inverted': this.inverted === true,
      'cat-c-text-link--xs': this.size === 'xs',
      'cat-c-text-link--sm': this.size === 'sm',
      'cat-c-text-link--lg': this.size === 'lg',
      'cat-c-text-link--xl': this.size === 'xl',
      'cat-c-text-link--has-link-after': this.slots.hasSlotted('linkAfter')
    });

    return html`
      ${this.slots.hasSlotted('custom-link')
        ? html`
            <div part="base" class="${componentClassName}" tabindex=${0} @keydown=${this.handleKeydown} aria-label="${ifDefined(this.arialabel)}">
              <slot part="part-custom-link" name="custom-link" tabindex=${-1}></slot>
            </div>
          `
        : html`
            <a
              part="base"
              href="${ifDefined(this.href)}"
              .target="${this.target?.toString() ?? ''}"
              class="${componentClassName}"
              @click="${this.isSkipLink ? this.handleClick : nothing}"
              aria-label="${ifDefined(this.arialabel)}"
            >
              <slot part="part-default"></slot>
              <div part="after" class="cat-c-text-link__after">
                <slot part="link-after" name="linkAfter"></slot>
              </div>
            </a>
          `}
    `;
  }
}

if (customElements.get('cat-text-link') === undefined) {
  customElements.define('cat-text-link', BlocksTextLink);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-text-link': BlocksTextLink;
  }
}
